/**
 * Where the Functions SDK should send callables.
 *
 * On Firebase Hosting (custom domain / *.web.app / *.firebaseapp.com) we point
 * at the page origin so requests stay same-origin via Hosting rewrites. That:
 * - avoids CORS preflight failures the browser mislabels when the network drops
 * - keeps callables reachable when a VPN blocks `*.cloudfunctions.net` (common
 *   from Cuba) while still allowing `alea-tui.com`
 *
 * Capacitor WebView (`https://localhost`) is not a Hosting origin. Production
 * APK builds fall back to `VITE_FUNCTIONS_ORIGIN` or `VITE_WEBAUTHN_ORIGIN` so
 * callables still use Hosting rewrites instead of `cloudfunctions.net`.
 *
 * Emulators and other non-Hosting origins keep the regional `cloudfunctions.net`
 * URL (region string passed to `getFunctions`).
 */

import { FUNCTIONS_REGION } from './emulators';

const HOSTING_HOST_RE =
  /^(?:[\w.-]+\.(?:web\.app|firebaseapp\.com)|(?:[\w.-]+\.)?(?:alca|alea)-tui\.(?:com|live|app))$/i;

export function isHostingWebOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    return HOSTING_HOST_RE.test(url.hostname);
  } catch {
    return false;
  }
}

function isCapacitorLocalOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      (url.protocol === 'https:' || url.protocol === 'capacitor:')
      && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
    );
  } catch {
    return false;
  }
}

const DEFAULT_HOSTING_ORIGIN = 'https://alea-tui.com';

function envHostingOrigin(): string | null {
  const candidates = [
    String(import.meta.env.VITE_FUNCTIONS_ORIGIN ?? '').trim(),
    String(import.meta.env.VITE_WEBAUTHN_ORIGIN ?? '').trim(),
  ];
  for (const value of candidates) {
    if (value && isHostingWebOrigin(value)) return value.replace(/\/$/, '');
  }
  return null;
}

/**
 * @param emulators Whether the Functions emulator is in use.
 * @param locationOrigin `window.location.origin` when running in a browser.
 * @param preferredHostingOrigin Healthy origin from GAPI probe (Capacitor failover).
 */
export function resolveFunctionsHost(
  emulators: boolean,
  locationOrigin: string | null | undefined = typeof window !== 'undefined'
    ? window.location.origin
    : null,
  preferredHostingOrigin?: string | null,
): string {
  if (emulators) return FUNCTIONS_REGION;
  if (locationOrigin && isHostingWebOrigin(locationOrigin)) return locationOrigin;
  if (locationOrigin && isCapacitorLocalOrigin(locationOrigin)) {
    // Always use production Hosting for Capacitor callables (never cloudfunctions.net).
    // Prefer the GAPI-probed healthy origin when available (com ↔ web.app failover).
    const preferred = String(preferredHostingOrigin ?? '').trim().replace(/\/$/, '');
    if (preferred && isHostingWebOrigin(preferred)) return preferred;
    return envHostingOrigin() ?? DEFAULT_HOSTING_ORIGIN;
  }
  return FUNCTIONS_REGION;
}
